import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Zap } from "lucide-react";
import { SIZES, IMAGES, formatBRL } from "../data/menu";

const LINES = [
  { text: "O AÇAÍ", cls: "text-white" },
  { text: "MAIS GIGA", cls: "text-[#A855F7]" },
  { text: "DE CURITIBA", cls: "text-white" },
];

const EASE = [0.22, 1, 0.36, 1];

export const Hero = ({ onNavigate }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section
      id="topo"
      ref={ref}
      className="relative flex min-h-[92vh] items-center overflow-hidden pb-20 pt-36 lg:pt-28"
    >
      <div className="pointer-events-none absolute -left-40 top-1/4 h-[480px] w-[480px] rounded-full bg-purple-600/25 blur-[140px]" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-[380px] w-[380px] rounded-full bg-fuchsia-600/15 blur-[120px]" />

      <div className="relative mx-auto grid w-full max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-[1.15fr_1fr] lg:px-8">
        <motion.div style={{ y: yText }}>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-8 flex items-center gap-2 font-grotesk text-xs font-bold uppercase tracking-[0.35em] text-amber-400"
          >
            <Zap className="h-4 w-4" /> Açaíteria · Curitiba
          </motion.p>

          <h1 className="font-syne font-extrabold leading-[0.95] tracking-tight">
            {LINES.map((l, i) => (
              <span key={l.text} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.2 + i * 0.14, duration: 1, ease: EASE }}
                  className={`block text-[2.45rem] sm:text-6xl lg:text-7xl ${l.cls}`}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.img
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.9, ease: EASE }}
            src={IMAGES.hero}
            alt="Copo de açaí Giga Açaí"
            className="mt-8 aspect-[16/10] w-full rounded-[1.75rem] border border-purple-400/30 object-cover sm:hidden"
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.8, ease: EASE }}
            className="mt-8 max-w-lg text-base sm:text-lg leading-relaxed text-purple-200/85"
          >
            Monte seu copo do jeito que quiser, escolha entre dezenas de
            adicionais e receba tudo pelo WhatsApp. Direto da loja, sem
            aplicativo, sem enrolação.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              type="button"
              data-testid="hero-cta-builder"
              onClick={() => onNavigate("#monte")}
              className="group flex items-center gap-2.5 rounded-full bg-emerald-400 px-8 py-4 font-grotesk text-sm font-bold uppercase tracking-[0.15em] text-[#1E053B] transition-transform duration-300 hover:scale-[1.04] active:scale-95"
            >
              Montar meu copo
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <button
              type="button"
              data-testid="hero-cta-roleta"
              onClick={() => onNavigate("#roleta")}
              className="rounded-full border border-purple-400/40 px-8 py-4 font-grotesk text-sm font-bold uppercase tracking-[0.15em] text-purple-100 transition-colors duration-300 hover:border-amber-300 hover:text-amber-300"
            >
              Roleta · R$ 54,90
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 1 }}
            className="mt-12 flex flex-wrap gap-2.5"
          >
            {SIZES.map((s) => (
              <span
                key={s.id}
                className="rounded-full border border-purple-500/25 bg-white/5 px-4 py-1.5 text-xs font-semibold text-purple-200/80"
              >
                {s.label} · <span className="text-emerald-400">{formatBRL(s.price)}</span>
              </span>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: yImg }}
          initial={{ opacity: 0, scale: 0.92, rotate: 6 }}
          animate={{ opacity: 1, scale: 1, rotate: 2 }}
          transition={{ delay: 0.5, duration: 1.1, ease: EASE }}
          className="relative mx-auto hidden w-full max-w-md sm:block"
        >
          <div className="absolute -inset-8 rounded-full bg-purple-500/30 blur-3xl" />
          <img
            src={IMAGES.hero}
            alt="Tigela de açaí cremoso com frutas e granola"
            className="relative aspect-[4/5] w-full rounded-[2.5rem] border border-purple-400/30 object-cover shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)]"
          />
          <motion.div
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -left-6 top-10 rounded-2xl bg-amber-400 px-5 py-3 font-syne text-sm font-extrabold text-[#1E053B] shadow-xl"
          >
            Açaí do dia · R$ 15
          </motion.div>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
            className="absolute -right-4 bottom-12 rounded-2xl border border-purple-400/30 bg-[#1E053B]/80 px-5 py-3 text-sm font-semibold text-purple-100 shadow-xl backdrop-blur-xl"
          >
            6 tamanhos · 300ml a 2L
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
